import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Zap, Brain, TrendingUp, Clock, Shield, Check, 
  ArrowRight, Menu, X, MessageSquare, Mail, CheckCircle, FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MatrixBackground from '../components/MatrixBackground';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LandingPage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    fetchPlans();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data || []);
    } catch (error) {
      console.error('Failed to fetch plans');
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    toast.success('Nachricht gesendet! Wir melden uns bald.');
    setContactForm({ name: '', email: '', message: '' });
  };

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#0B0F14', color: '#E6EDF3' }}>
      {/* Navigation */}
      <nav 
        className="fixed top-0 w-full z-50 transition-all duration-300"
        style={{
          backgroundColor: scrollY > 50 ? '#0F141Aee' : 'transparent',
          backdropFilter: scrollY > 50 ? 'blur(10px)' : 'none',
          borderBottom: scrollY > 50 ? '1px solid #1F2937' : 'none'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ color: '#7CD4FD', fontFamily: 'Manrope' }}>
            Carcortex-ai
          </h1>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {/* Toggle Diagnose/FMS */}
            <div 
              className="flex rounded-lg p-1"
              style={{ backgroundColor: '#1F2937' }}
            >
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 rounded-lg transition-all text-sm"
                style={{
                  backgroundColor: '#1EA0E6',
                  color: '#ffffff'
                }}
              >
                Diagnose
              </button>
              <button
                onClick={() => navigate('/fms')}
                className="px-4 py-2 rounded-lg transition-all text-sm"
                style={{
                  backgroundColor: 'transparent',
                  color: '#9CA3AF'
                }}
              >
                FMS
              </button>
            </div>

            <a href="#features" className="hover:text-blue-400 transition">Features</a>
            <a href="#pricing" className="hover:text-blue-400 transition">Preise</a>
            <a href="#contact" className="hover:text-blue-400 transition">Kontakt</a>
            <Button
              onClick={() => navigate('/auth')}
              style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
            >
              Login
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden p-6 space-y-4" style={{ backgroundColor: '#0F141A', borderTop: '1px solid #1F2937' }}>
            {/* Mobile Toggle */}
            <div className="space-y-2 pb-4 border-b border-gray-700">
              <button
                onClick={() => navigate('/')}
                className="w-full text-left px-4 py-2 rounded-lg transition-all"
                style={{
                  backgroundColor: '#1EA0E6',
                  color: '#ffffff'
                }}
              >
                Diagnose
              </button>
              <button
                onClick={() => {
                  navigate('/fms');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 rounded-lg transition-all"
                style={{
                  backgroundColor: '#1F2937',
                  color: '#ffffff'
                }}
              >
                FMS (Fuhrpark)
              </button>
            </div>

            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block">Features</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block">Preise</a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block">Kontakt</a>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full"
              style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
            >
              Login
            </Button>
          </div>
        )}
      </nav>

      {/* Hero Section with Parallax */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ zIndex: 1 }}>
        {/* Animated Background */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1707286972301-a0b06f3f4870)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15,
            transform: `scale(${1 + scrollY / 2000})`,
            filter: 'blur(8px)'
          }}
        />
        
        {/* Gradient Overlays */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 20% 50%, #1EA0E640 0%, transparent 50%), radial-gradient(circle at 80% 80%, #7CD4FD40 0%, transparent 50%)',
            transform: `translateY(${scrollY * 0.5}px)`
          }}
        />
        
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, #0B0F1400 0%, #0B0F14 100%)'
          }}
        />
        
        <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
          <div 
            style={{ 
              transform: `translateY(${scrollY * 0.3}px)`,
              opacity: 1 - (scrollY / 500)
            }}
          >
            {/* Badge */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: '#1EA0E620', border: '1px solid #1EA0E6' }}
            >
              <Zap className="w-4 h-4" style={{ color: '#7CD4FD' }} />
              <span className="text-sm font-semibold" style={{ color: '#7CD4FD' }}>
                Powered by Gemini 2.5 Pro AI
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Manrope', lineHeight: '1.2' }}>
              <span style={{ color: '#E6EDF3' }}>KI-gestützte</span><br/>
              <span 
                style={{ 
                  background: 'linear-gradient(135deg, #7CD4FD 0%, #1EA0E6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Fahrzeugdiagnose
              </span><br/>
              <span style={{ color: '#E6EDF3' }}>in Sekunden</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto" style={{ color: '#94A3B8' }}>
              Professionelle Diagnosen ohne teures Equipment. Spare Zeit, verdiene mehr – für Werkstätten und Hobby-Schrauber.
            </p>

            {/* Stats Animation */}
            <div className="grid grid-cols-3 gap-8 mb-12 max-w-3xl mx-auto">
              <div 
                className="p-4 rounded-xl"
                style={{ 
                  backgroundColor: '#22C55E10',
                  border: '1px solid #22C55E30',
                  transform: `translateY(${Math.sin(scrollY / 100) * 5}px)`
                }}
              >
                <div className="text-4xl font-bold" style={{ color: '#22C55E' }}>-80%</div>
                <div className="text-sm" style={{ color: '#94A3B8' }}>Diagnosezeit</div>
              </div>
              <div 
                className="p-4 rounded-xl"
                style={{ 
                  backgroundColor: '#7CD4FD10',
                  border: '1px solid #7CD4FD30',
                  transform: `translateY(${Math.sin((scrollY + 50) / 100) * 5}px)`
                }}
              >
                <div className="text-4xl font-bold" style={{ color: '#7CD4FD' }}>+300%</div>
                <div className="text-sm" style={{ color: '#94A3B8' }}>Effizienz</div>
              </div>
              <div 
                className="p-4 rounded-xl"
                style={{ 
                  backgroundColor: '#F59E0B10',
                  border: '1px solid #F59E0B30',
                  transform: `translateY(${Math.sin((scrollY + 100) / 100) * 5}px)`
                }}
              >
                <div className="text-4xl font-bold" style={{ color: '#F59E0B' }}>+50%</div>
                <div className="text-sm" style={{ color: '#94A3B8' }}>Umsatz</div>
              </div>
            </div>

            <div className="flex gap-4 justify-center flex-wrap">
              <Button
                size="lg"
                onClick={() => navigate('/signup')}
                className="group"
                style={{ 
                  backgroundColor: '#1EA0E6', 
                  color: '#fff', 
                  fontSize: '1.1rem', 
                  padding: '1.5rem 3rem',
                  boxShadow: '0 10px 40px rgba(30, 160, 230, 0.3)'
                }}
              >
                Jetzt starten
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                style={{ 
                  borderColor: '#1F2937', 
                  color: '#E6EDF3', 
                  fontSize: '1.1rem', 
                  padding: '1.5rem 3rem',
                  backgroundColor: '#0B0F1480',
                  backdropFilter: 'blur(10px)'
                }}
              >
                Preise ansehen
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce"
          style={{ opacity: 1 - (scrollY / 300) }}
        >
          <div className="w-6 h-10 rounded-full border-2" style={{ borderColor: '#7CD4FD' }}>
            <div className="w-1 h-3 rounded-full mx-auto mt-2" style={{ backgroundColor: '#7CD4FD' }} />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 relative" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              Warum <span style={{ color: '#7CD4FD' }}>Carcortex-ai</span>?
            </h2>
            <p className="text-xl" style={{ color: '#94A3B8' }}>
              Modernste KI trifft auf 30+ Jahre Werkstatt-Erfahrung
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Brain className="w-12 h-12" />,
                color: '#7CD4FD',
                title: 'Gemini 2.5 Pro KI',
                desc: 'Modernste AI analysiert fahrzeugspezifisch jedes Problem mit Experten-Präzision'
              },
              {
                icon: <Zap className="w-12 h-12" />,
                color: '#F59E0B',
                title: 'Blitzschnell',
                desc: 'Von Symptom zur Lösung in Minuten statt Stunden. Spare 80% Diagnosezeit'
              },
              {
                icon: <TrendingUp className="w-12 h-12" />,
                color: '#22C55E',
                title: 'Mehr Umsatz',
                desc: 'Bis zu 50% mehr Aufträge durch schnellere Durchlaufzeit'
              },
              {
                icon: <Shield className="w-12 h-12" />,
                color: '#A78BFA',
                title: 'Step-by-Step',
                desc: 'Detaillierte Anleitungen – auch ohne Profi-Wissen durchführbar'
              },
              {
                icon: <Clock className="w-12 h-12" />,
                color: '#22D3EE',
                title: 'Live-Recherche',
                desc: 'Greift auf aktuelle Web-Daten zu. Immer up-to-date'
              },
              {
                icon: <MessageSquare className="w-12 h-12" />,
                color: '#1EA0E6',
                title: 'Lernend',
                desc: 'Wird mit jedem Fall besser. Wissensdatenbank wächst automatisch'
              }
            ].map((feature, idx) => (
              <Card 
                key={idx}
                className="group hover:scale-105 transition-all duration-300"
                style={{ 
                  backgroundColor: '#0F141A', 
                  borderColor: '#1F2937',
                  transform: `translateY(${Math.sin((scrollY + idx * 100) / 100) * 10}px)`
                }}
              >
                <CardHeader>
                  <div className="mb-4" style={{ color: feature.color }}>
                    {feature.icon}
                  </div>
                  <CardTitle style={{ color: '#E6EDF3' }}>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p style={{ color: '#94A3B8' }}>{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Preview with Images */}
      <section className="py-32 relative overflow-hidden" style={{ backgroundColor: '#0B0F14', zIndex: 1 }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              Professionelle <span style={{ color: '#7CD4FD' }}>Diagnose-Software</span>
            </h2>
            <p className="text-xl" style={{ color: '#94A3B8' }}>
              Modernste Technologie für präzise Fahrzeugdiagnosen
            </p>
          </div>

          {/* Image Grid with Parallax */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div 
              className="relative group overflow-hidden rounded-2xl"
              style={{
                transform: `translateY(${Math.sin(scrollY / 200) * 20}px)`,
                transition: 'transform 0.3s ease-out'
              }}
            >
              <img 
                src="https://images.unsplash.com/photo-1687054238593-0a20022e5295"
                alt="Moderne Dashboard-Ansicht"
                className="w-full h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                style={{ borderRadius: '16px' }}
              />
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                style={{ background: 'linear-gradient(to top, #0B0F14ee 0%, transparent 100%)' }}
              >
                <div className="text-center p-6">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#E6EDF3' }}>
                    Intuitive Benutzeroberfläche
                  </h3>
                  <p style={{ color: '#94A3B8' }}>
                    Übersichtliches Dashboard mit allen wichtigen Informationen
                  </p>
                </div>
              </div>
            </div>

            <div 
              className="relative group overflow-hidden rounded-2xl"
              style={{
                transform: `translateY(${Math.sin((scrollY + 100) / 200) * 20}px)`,
                transition: 'transform 0.3s ease-out'
              }}
            >
              <img 
                src="https://images.unsplash.com/photo-1510661625376-96402d21c828"
                alt="Diagnose in der Werkstatt"
                className="w-full h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                style={{ borderRadius: '16px' }}
              />
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                style={{ background: 'linear-gradient(to top, #0B0F14ee 0%, transparent 100%)' }}
              >
                <div className="text-center p-6">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#E6EDF3' }}>
                    Praxisnah & Detailliert
                  </h3>
                  <p style={{ color: '#94A3B8' }}>
                    Schritt-für-Schritt Anleitungen direkt umsetzbar
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Highlights with Icons */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl" style={{ backgroundColor: '#0F141A', border: '1px solid #1F2937' }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#1EA0E620' }}>
                <CheckCircle className="w-8 h-8" style={{ color: '#22C55E' }} />
              </div>
              <h4 className="text-xl font-bold mb-2" style={{ color: '#E6EDF3' }}>99.2% Genauigkeit</h4>
              <p style={{ color: '#94A3B8' }}>KI-gestützte Diagnosen mit höchster Präzision</p>
            </div>

            <div className="text-center p-8 rounded-2xl" style={{ backgroundColor: '#0F141A', border: '1px solid #1F2937' }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#1EA0E620' }}>
                <Clock className="w-8 h-8" style={{ color: '#F59E0B' }} />
              </div>
              <h4 className="text-xl font-bold mb-2" style={{ color: '#E6EDF3' }}>Unter 5 Minuten</h4>
              <p style={{ color: '#94A3B8' }}>Von Symptom zur vollständigen Diagnose</p>
            </div>

            <div className="text-center p-8 rounded-2xl" style={{ backgroundColor: '#0F141A', border: '1px solid #1F2937' }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#1EA0E620' }}>
                <Shield className="w-8 h-8" style={{ color: '#7CD4FD' }} />
              </div>
              <h4 className="text-xl font-bold mb-2" style={{ color: '#E6EDF3' }}>500+ Fahrzeugmodelle</h4>
              <p style={{ color: '#94A3B8' }}>Umfassende Datenbank aller gängigen Marken</p>
            </div>
          </div>
        </div>
      </section>

      {/* Live Demo Showcase */}
      <section className="py-32" style={{ backgroundColor: '#0F141A', zIndex: 1 }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              So <span style={{ color: '#7CD4FD' }}>einfach</span> funktioniert's
            </h2>
            <p className="text-xl" style={{ color: '#94A3B8' }}>
              Sehen Sie selbst, wie intuitiv unsere Plattform ist
            </p>
          </div>

          {/* Step 1: Dashboard */}
          <div className="mb-24">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div 
                style={{ 
                  transform: `translateX(${Math.sin(scrollY / 300) * -20}px)`,
                  transition: 'transform 0.3s ease-out'
                }}
              >
                <span className="text-5xl font-bold" style={{ color: '#1F2937', WebkitTextStroke: '2px #7CD4FD' }}>
                  01
                </span>
                <h3 className="text-3xl font-bold mt-4 mb-4" style={{ color: '#E6EDF3' }}>
                  Übersichtliches Dashboard
                </h3>
                <p className="text-lg mb-6" style={{ color: '#94A3B8' }}>
                  Alle wichtigen Informationen auf einen Blick. Starten Sie direkt mit der Diagnose oder verwalten Sie Ihre Fahrzeuge.
                </p>
                <ul className="space-y-3">
                  {['Schnellzugriff auf alle Funktionen', 'Übersicht Ihrer Sessions', 'Abo-Status im Blick'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div 
                className="rounded-2xl overflow-hidden"
                style={{ 
                  backgroundColor: '#0B0F14',
                  border: '2px solid #1F2937',
                  boxShadow: '0 20px 60px rgba(124, 212, 253, 0.1)',
                  transform: `translateX(${Math.sin(scrollY / 300) * 20}px)`,
                  transition: 'transform 0.3s ease-out'
                }}
              >
                {/* Mock Dashboard */}
                <div className="p-8" style={{ backgroundColor: '#0B0F14' }}>
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-xl font-bold" style={{ color: '#E6EDF3' }}>Dashboard</h4>
                    <div className="px-3 py-1 rounded-full text-sm" style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>
                      50 Sessions übrig
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#0F141A', border: '1px solid #1F2937' }}>
                      <p className="text-sm mb-1" style={{ color: '#94A3B8' }}>Diagnosen</p>
                      <p className="text-2xl font-bold" style={{ color: '#7CD4FD' }}>23</p>
                    </div>
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#0F141A', border: '1px solid #1F2937' }}>
                      <p className="text-sm mb-1" style={{ color: '#94A3B8' }}>Fahrzeuge</p>
                      <p className="text-2xl font-bold" style={{ color: '#7CD4FD' }}>5</p>
                    </div>
                  </div>
                  <button 
                    className="w-full py-3 rounded-lg font-semibold"
                    style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                  >
                    + Neue Diagnose starten
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2: Diagnosis Interface */}
          <div className="mb-24">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div 
                className="rounded-2xl overflow-hidden order-2 md:order-1"
                style={{ 
                  backgroundColor: '#0B0F14',
                  border: '2px solid #1F2937',
                  boxShadow: '0 20px 60px rgba(124, 212, 253, 0.1)',
                  transform: `translateX(${Math.sin((scrollY + 200) / 300) * -20}px)`,
                  transition: 'transform 0.3s ease-out'
                }}
              >
                {/* Mock Diagnosis */}
                <div className="p-8" style={{ backgroundColor: '#0B0F14' }}>
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: '#1EA0E6', color: '#fff' }}>1</span>
                      <h4 className="font-bold" style={{ color: '#7CD4FD' }}>Prüfschritt</h4>
                    </div>
                    <p className="text-sm mb-3" style={{ color: '#E6EDF3' }}>
                      Prüfen Sie die Sicherungen im Motorraum
                    </p>
                    <div className="p-3 rounded" style={{ backgroundColor: '#0F141A' }}>
                      <p className="text-xs mb-1" style={{ color: '#7CD4FD' }}>Lokalisierung:</p>
                      <p className="text-xs" style={{ color: '#94A3B8' }}>Sicherungskasten im Motorraum, linke Seite</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="flex-1 py-2 rounded text-sm font-semibold" style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>
                      ✓ Gut
                    </button>
                    <button className="flex-1 py-2 rounded text-sm font-semibold" style={{ backgroundColor: '#EF444420', color: '#EF4444' }}>
                      ✗ Schlecht
                    </button>
                    <button className="flex-1 py-2 rounded text-sm font-semibold" style={{ backgroundColor: '#F59E0B20', color: '#F59E0B' }}>
                      ? Unklar
                    </button>
                  </div>
                </div>
              </div>
              <div 
                className="order-1 md:order-2"
                style={{ 
                  transform: `translateX(${Math.sin((scrollY + 200) / 300) * 20}px)`,
                  transition: 'transform 0.3s ease-out'
                }}
              >
                <span className="text-5xl font-bold" style={{ color: '#1F2937', WebkitTextStroke: '2px #7CD4FD' }}>
                  02
                </span>
                <h3 className="text-3xl font-bold mt-4 mb-4" style={{ color: '#E6EDF3' }}>
                  Intelligente Schritt-für-Schritt Diagnose
                </h3>
                <p className="text-lg mb-6" style={{ color: '#94A3B8' }}>
                  Die KI führt Sie durch präzise Prüfschritte. Jeder Schritt ist fahrzeugspezifisch und enthält detaillierte Anleitungen.
                </p>
                <ul className="space-y-3">
                  {['Klare Anweisungen mit Lokalisierung', 'Sicherheitshinweise integriert', 'Einfache Rückmeldung mit Buttons'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Step 3: Report */}
          <div>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div 
                style={{ 
                  transform: `translateX(${Math.sin((scrollY + 400) / 300) * -20}px)`,
                  transition: 'transform 0.3s ease-out'
                }}
              >
                <span className="text-5xl font-bold" style={{ color: '#1F2937', WebkitTextStroke: '2px #7CD4FD' }}>
                  03
                </span>
                <h3 className="text-3xl font-bold mt-4 mb-4" style={{ color: '#E6EDF3' }}>
                  Professioneller PDF-Report
                </h3>
                <p className="text-lg mb-6" style={{ color: '#94A3B8' }}>
                  Am Ende erhalten Sie einen detaillierten Bericht mit allen durchgeführten Tests und der finalen Diagnose. Perfekt für Ihre Unterlagen.
                </p>
                <ul className="space-y-3">
                  {['Vollständige Dokumentation', 'Druckfertig formatiert', 'Fahrzeugdaten & Briefkopf'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div 
                className="rounded-2xl overflow-hidden"
                style={{ 
                  backgroundColor: '#0B0F14',
                  border: '2px solid #1F2937',
                  boxShadow: '0 20px 60px rgba(124, 212, 253, 0.1)',
                  transform: `translateX(${Math.sin((scrollY + 400) / 300) * 20}px)`,
                  transition: 'transform 0.3s ease-out'
                }}
              >
                {/* Mock Report */}
                <div className="p-8" style={{ backgroundColor: '#0B0F14' }}>
                  <div className="text-center mb-6">
                    <FileText className="w-12 h-12 mx-auto mb-3" style={{ color: '#7CD4FD' }} />
                    <h4 className="text-xl font-bold mb-2" style={{ color: '#E6EDF3' }}>Diagnose-Report</h4>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>BMW 320i | 02.11.2024</p>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="p-3 rounded" style={{ backgroundColor: '#0F141A', border: '1px solid #1F2937' }}>
                      <p className="text-xs mb-1" style={{ color: '#7CD4FD' }}>Symptom:</p>
                      <p className="text-xs" style={{ color: '#E6EDF3' }}>Motor startet nicht</p>
                    </div>
                    <div className="p-3 rounded" style={{ backgroundColor: '#EF444420', borderLeft: '3px solid #EF4444' }}>
                      <p className="text-xs mb-1 font-semibold" style={{ color: '#EF4444' }}>Diagnose:</p>
                      <p className="text-xs" style={{ color: '#E6EDF3' }}>Defekte Zündspule Zylinder 3</p>
                    </div>
                  </div>
                  <button 
                    className="w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                  >
                    <FileText className="w-4 h-4" />
                    PDF herunterladen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-32" style={{ backgroundColor: '#0F141A', zIndex: 1 }}>
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-20" style={{ fontFamily: 'Manrope' }}>
            So einfach geht's
          </h2>

          <div className="space-y-16">
            {[
              { step: '01', title: 'Symptome eingeben', desc: 'Beschreibe das Problem in eigenen Worten' },
              { step: '02', title: 'KI analysiert', desc: 'Gemini 2.5 Pro generiert fahrzeugspezifische Tests' },
              { step: '03', title: 'Tests durchführen', desc: 'Folge den detaillierten Step-by-Step Anleitungen' },
              { step: '04', title: 'Problem gelöst', desc: 'Erhalte Reparaturanleitung + PDF-Report' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-8">
                <div 
                  className="text-6xl font-bold"
                  style={{ 
                    color: '#1F2937',
                    WebkitTextStroke: '2px #7CD4FD'
                  }}
                >
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#E6EDF3' }}>{item.title}</h3>
                  <p style={{ color: '#94A3B8' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing with CTA */}
      <section id="pricing" className="py-32" style={{ zIndex: 1, position: 'relative' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              Transparente <span style={{ color: '#7CD4FD' }}>Preise</span>
            </h2>
            <p className="text-xl" style={{ color: '#94A3B8' }}>
              Wähle den Plan, der zu dir passt
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, idx) => (
              <Card 
                key={plan.id}
                className="relative hover:scale-105 transition-all duration-300"
                style={{
                  backgroundColor: '#0F141A',
                  borderColor: idx === 1 ? '#1EA0E6' : '#1F2937',
                  borderWidth: idx === 1 ? '2px' : '1px'
                }}
              >
                {idx === 1 && (
                  <div 
                    className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-sm font-bold"
                    style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                  >
                    BELIEBT
                  </div>
                )}
                <CardHeader>
                  <CardTitle style={{ color: '#E6EDF3', fontSize: '1.5rem' }}>{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold" style={{ color: '#7CD4FD' }}>
                      €{plan.price}
                    </span>
                    <span style={{ color: '#94A3B8' }}>/Monat</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                      <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                      {plan.limits?.sessions_per_month} Diagnosen/Monat
                    </li>
                    {plan.features?.pdf_reports && (
                      <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                        <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                        PDF Reports
                      </li>
                    )}
                    {plan.features?.advanced_diagnosis && (
                      <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                        <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                        Erweiterte KI-Diagnose
                      </li>
                    )}
                    {plan.features?.web_search && (
                      <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                        <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                        Live Web-Recherche
                      </li>
                    )}
                    {plan.features?.priority_support && (
                      <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                        <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                        Priority Support
                      </li>
                    )}
                  </ul>
                  <Button
                    className="w-full"
                    onClick={() => navigate('/auth')}
                    style={{
                      backgroundColor: idx === 1 ? '#1EA0E6' : '#1F2937',
                      color: '#fff'
                    }}
                  >
                    Jetzt abonnieren
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-32" style={{ backgroundColor: '#0F141A', zIndex: 1, position: 'relative' }}>
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              Kontakt aufnehmen
            </h2>
            <p style={{ color: '#94A3B8' }}>
              Fragen? Wir helfen gerne weiter!
            </p>
          </div>

          <Card style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937' }}>
            <CardContent className="p-8">
              <form onSubmit={handleContact} className="space-y-6">
                <div>
                  <label className="block mb-2" style={{ color: '#E6EDF3' }}>Name</label>
                  <Input
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                    style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ color: '#E6EDF3' }}>E-Mail</label>
                  <Input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                    style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ color: '#E6EDF3' }}>Nachricht</label>
                  <Textarea
                    rows={5}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                    style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Nachricht senden
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t" style={{ borderColor: '#1F2937', zIndex: 1, position: 'relative' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold mb-4" style={{ color: '#7CD4FD' }}>Carcortex-ai</h3>
              <p className="text-sm" style={{ color: '#94A3B8' }}>
                KI-gestützte Fahrzeugdiagnose für Profis und Enthusiasten
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4" style={{ color: '#E6EDF3' }}>Produkt</h4>
              <div className="space-y-2 text-sm" style={{ color: '#94A3B8' }}>
                <a href="#features" className="block hover:text-blue-400">Features</a>
                <a href="#pricing" className="block hover:text-blue-400">Preise</a>
                <a href="/auth" className="block hover:text-blue-400">Login</a>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4" style={{ color: '#E6EDF3' }}>Rechtliches</h4>
              <div className="space-y-2 text-sm" style={{ color: '#94A3B8' }}>
                <a href="/agb" className="block hover:text-blue-400">AGB</a>
                <a href="/datenschutz" className="block hover:text-blue-400">Datenschutz</a>
                <a href="/impressum" className="block hover:text-blue-400">Impressum</a>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4" style={{ color: '#E6EDF3' }}>Kontakt</h4>
              <div className="space-y-2 text-sm" style={{ color: '#94A3B8' }}>
                <a href="#contact" className="block hover:text-blue-400">Nachricht senden</a>
                <p>support@autodiag.ai</p>
              </div>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t text-center text-sm" style={{ borderColor: '#1F2937', color: '#94A3B8' }}>
            © 2024 Carcortex-ai. Alle Rechte vorbehalten.
          </div>
        </div>
      </footer>
    </div>
  );
};
